import { useState, useEffect } from 'react';
import { url } from './url.js';
import { IconPhone, IconClock, IconPin, IconShield, PEST_NAV_ICONS } from './icons.jsx';
import { REVIEWS, GOOGLE_REVIEWS_URL, OPENING_HOURS, PESTS } from './data.js';
import { getGoogleAggregate } from './google-reviews.js';

export function Logo({ inline = false }) {
  return (
    <a
      href={url.home()}
      className="logo"
      aria-label="ABC Ongediertebestrijding — homepage"
      title="ABC Ongediertebestrijding"
    >
      <span className="logo-mark">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2 L 4 6 L 4 12 C 4 17.5 7.5 21 12 22 C 16.5 21 20 17.5 20 12 L 20 6 Z M 12 6.5 L 16 8.5 V 12 C 16 14.8 14.3 16.8 12 17.5 C 9.7 16.8 8 14.8 8 12 V 8.5 Z" fill="white"/>
        </svg>
      </span>
      <span className="logo-text">
        {/* Spatie tussen 'ABC' en 'Ongedierte' zit expliciet in JSX-text
            zodat geserialiseerde anchor-text correct uit elkaar wordt
            gelezen door screen-readers en SEO-crawlers. Onzichtbare
            scheidings-spatie tussen titel en subtitel ('Ongediertebestrijding'
            ↔ 'Professionele bestrijding') voorkomt klontering wanneer
            CSS faalt of de span-display als inline rendert. */}
        <span className="logo-title">ABC <span className="accent">Ongedierte</span></span>
        <span className="logo-sep" aria-hidden="true"> </span>
        <span className="logo-sub">Professionele bestrijding</span>
      </span>
    </a>
  );
}

function TopBar() {
  // Live Google review-aggregaat: prerendered HTML toont fallback uit data.js,
  // bij client-mount fetchen we de actuele cijfers via getGoogleAggregate
  // (zelfde gedrag als RatingBar elders op de site). Hierdoor is het topbar-
  // cijfer altijd up-to-date zonder rebuild.
  const [agg, setAgg] = useState({ rating: REVIEWS.rating, total: REVIEWS.total });
  useEffect(() => {
    let alive = true;
    getGoogleAggregate().then(data => {
      if (!alive || !data) return;
      if (data.rating !== agg.rating || data.total !== agg.total) {
        setAgg({ rating: data.rating, total: data.total });
      }
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const ratingStr = String(agg.rating).replace('.', ',');
  return (
    <div className="topbar">
      <div className="container">
        <div className="topbar-inner">
          {/* Linker-blok: openingstijden + werkgebied. Mobile-audit fase 5:
              CSS verbergt deze op <1024px (premium real estate boven de
              vouw). Op desktop ongewijzigd. */}
          <div className="tb-left tb-desktop-only">
            <div className="tbi"><IconClock /> {OPENING_HOURS.weekdayLabel} · {OPENING_HOURS.emergencyLabel}</div>
            <div className="tbi"><IconPin width="14" height="14" /> Actief in Zuid-Holland, Noord-Holland &amp; Utrecht</div>
          </div>
          <div className="topbar-right">
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="tbi tbi-rating"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'rgba(255,255,255,0.85)', fontWeight: 600, textDecoration: 'none' }}
              aria-label={`${ratingStr} sterren op ${agg.total} Google reviews, bekijk op Google`}
              title="Bekijk al onze Google reviews"
            >
              <span style={{ color: '#FBBF24' }}>★★★★★</span> {ratingStr} · {agg.total}
            </a>
            {/* Gecertificeerde partners-chip: desktop-only (zit ook al in footer + hero). */}
            <span className="tb-desktop-only" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
              <IconShield width="14" height="14" /> Gecertificeerde partners
            </span>
            <a href="tel:0855005050"><IconPhone width="14" height="14" /> 085 500 50 50</a>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Header({ routeType }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Diensten-submenu open/dicht state (mobile click-toggle).
  // Op desktop opent het submenu via CSS :hover en blijft deze state false.
  const [dienstenOpen, setDienstenOpen] = useState(false);

  // Plagen alfabetisch sorteren voor het Diensten-submenu.
  // Locale 'nl' zorgt dat Nederlandse spelling correct sorteert.
  const sortedPests = [...PESTS].sort((a, b) => a.nl.localeCompare(b.nl, 'nl'));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    // Body-class trick: CSS gebruikt body.drawer-open om de floating
    // bel-knop te verbergen. Was eerder :has() selector maar die werkt
    // niet op oudere iOS Safari (<15.4) — class-toggle is universeel.
    document.body.classList.toggle('drawer-open', mobileOpen);
    return () => document.body.classList.remove('drawer-open');
  }, [mobileOpen]);

  // Switch nav links: when on home, anchor-link to sections; on other pages, route home then anchor.
  const isHome = routeType === "home";
  const link = (anchor) => isHome ? `#${anchor}` : `#/#${anchor}`;

  // Diensten wordt apart gerenderd met submenu (alle plagen).
  // De overige nav-links blijven gewone single-level items.
  const navItems = [
    { href: url.spoed(),       label: "Spoedservice" },
    { href: url.werkgebied(),  label: "Werkgebied" },
    { href: url.blog(),        label: "Blog" },
    { href: url.reviews(),     label: "Reviews" },
    { href: url.horeca(),      label: "Horeca" },
    { href: url.contact(),     label: "Contact" },
  ];

  const closeAllMenus = () => {
    setMobileOpen(false);
    setDienstenOpen(false);
  };

  // ÉÉN <nav>-element, twee CSS-states (mobile-audit fase 1).
  // Desktop (≥1024px): horizontale balk in de header.
  // Mobile (<1024px): drawer geschoven van rechts via .is-drawer-open class.
  // Screen readers zien dus één nav, Google ziet één link-set, DOM is kleiner.
  return (
    <>
      <TopBar />
      <header className={"header" + (scrolled ? " scrolled" : "")}>
        <div className="container">
          <div className="header-inner">
            <Logo />
            <nav
              className={"nav" + (mobileOpen ? " is-drawer-open" : "")}
              id="primary-nav"
              aria-label="Hoofdmenu"
            >
              {/* Drawer-header: alleen zichtbaar op mobile via CSS.
                  Bevat compact merk-label + sluitknop voor visuele balans. */}
              <div className="nav-drawer-header">
                <span className="nav-drawer-brand">
                  <span className="nav-drawer-brand-mark" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M12 2 L 4 6 L 4 12 C 4 17.5 7.5 21 12 22 C 16.5 21 20 17.5 20 12 L 20 6 Z M 12 6.5 L 16 8.5 V 12 C 16 14.8 14.3 16.8 12 17.5 C 9.7 16.8 8 14.8 8 12 V 8.5 Z" />
                    </svg>
                  </span>
                  <span>ABC <span style={{ color: 'var(--orange)' }}>Ongedierte</span></span>
                </span>
                <button
                  type="button"
                  className="nav-close"
                  onClick={() => setMobileOpen(false)}
                  aria-label="Sluit menu"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                    <path d="M6 6l12 12M18 6l-12 12"/>
                  </svg>
                </button>
              </div>
              {/* Diensten met uitklap-submenu: alle plagen alfabetisch.
                  Mobile: klik op label toggled submenu (preventDefault op
                  link-navigatie). Desktop: hover-dropdown via CSS, label
                  blijft klikbaar naar /diensten/. */}
              <div className={`nav-link-group ${dienstenOpen ? 'is-open' : ''}`}>
                <a
                  href={url.diensten()}
                  className="nav-link nav-link-with-sub"
                  onClick={(e) => {
                    // Mobile (< 1024px): toggle submenu, navigatie naar
                    // /diensten/ alleen via expliciete "Bekijk alle diensten"
                    // link onderin de submenu. Desktop: navigeer direct.
                    if (window.innerWidth < 1024) {
                      e.preventDefault();
                      setDienstenOpen(o => !o);
                    } else {
                      closeAllMenus();
                    }
                  }}
                  aria-expanded={dienstenOpen}
                  aria-controls="nav-submenu-diensten"
                >
                  <span className="nav-link-label">Diensten</span>
                  <svg
                    className={`nav-sub-chevron ${dienstenOpen ? 'is-open' : ''}`}
                    width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </a>
                <div className="nav-submenu" id="nav-submenu-diensten" role="menu">
                  {sortedPests.map(p => {
                    const NavIcon = PEST_NAV_ICONS[p.slug];
                    return (
                      <a
                        key={p.slug}
                        href={url.plaag(p.slug)}
                        onClick={closeAllMenus}
                        className="nav-submenu-link"
                        role="menuitem"
                      >
                        {/* Silhouet-icoontje alleen op desktop (CSS verbergt 'm
                            op <1024px). Mobile-stijl blijft tekst-only zoals
                            verzocht 2026-06-02. */}
                        {NavIcon && (
                          <span className="nav-submenu-icon" aria-hidden="true">
                            <NavIcon />
                          </span>
                        )}
                        <span className="nav-submenu-text">{p.nl}</span>
                      </a>
                    );
                  })}
                  <a
                    href={url.diensten()}
                    onClick={closeAllMenus}
                    className="nav-submenu-link nav-submenu-link-all"
                    role="menuitem"
                  >
                    Bekijk alle diensten →
                  </a>
                </div>
              </div>

              {navItems.map(it => (
                <a
                  key={it.label}
                  href={it.href}
                  onClick={() => setMobileOpen(false)}
                  className="nav-link"
                >
                  <span className="nav-link-label">{it.label}</span>
                  <svg className="nav-link-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </a>
              ))}
              {/* Mobile-only bel-CTA in de drawer. Vervangt de oude .mnb
                  + voorkomt dubbele bottom-CTA (mobile-audit fase 2).
                  Card-style: icoon + nummer + sublabel (openingstijden). */}
              <a
                href="tel:0855005050"
                className="nav-drawer-call"
                onClick={() => setMobileOpen(false)}
              >
                <span className="nav-drawer-call-icon" aria-hidden="true">
                  <IconPhone width="18" height="18" />
                </span>
                <span className="nav-drawer-call-text">
                  <span className="nav-drawer-call-num">085 500 50 50</span>
                  <span className="nav-drawer-call-sub">Spoeddienst 24/7 · {OPENING_HOURS.weekdayLabel.replace('Ma-Vr ', 'Ma-Vr ')}</span>
                </span>
              </a>
            </nav>
            {/* Backdrop voor de drawer — alleen mobile zichtbaar via CSS.
                Klik buiten drawer sluit menu. */}
            <div
              className={"nav-backdrop" + (mobileOpen ? " is-open" : "")}
              onClick={() => setMobileOpen(false)}
              aria-hidden="true"
            />
            <div className="header-actions">
              <a href={url.offerte()} className="btn-offerte">Offerte aanvragen</a>
              <a href="tel:0855005050" className="btn-bel">
                <IconPhone width="16" height="16" /> 085 500 50 50
              </a>
              <button
                className="hamburger"
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
                aria-controls="primary-nav"
                aria-expanded={mobileOpen}
              >
                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path d="M3 12h18M3 6h18M3 18h18"/></svg>
              </button>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}

